type ApiResponse<T> = {
  ok: boolean;
  data: T | null;
  error: string | null;
  status: number;
};

export type UnwrapOptions = {
  // When true (the default), a 401 sends the browser to sign in. Background
  // work such as the bell's unread poll passes false so an expired session
  // never pulls the user off a page they are in the middle of.
  redirectOnUnauthorized?: boolean;
};

export type ApiError = Error & { status?: number };

export class UnauthorizedError extends Error {
  status = 401;

  constructor(message = 'Unauthorized') {
    super(message);
    this.name = 'UnauthorizedError';
    Object.setPrototypeOf(this, UnauthorizedError.prototype);
  }
}

export const isUnauthorizedError = (error: unknown): error is UnauthorizedError =>
  error instanceof UnauthorizedError;

export const unwrap = <T>(response: ApiResponse<T>, options: UnwrapOptions = {}): T => {
  const { redirectOnUnauthorized = true } = options;

  if (response.status === 401) {
    if (redirectOnUnauthorized && typeof window !== 'undefined') {
      const body = response.data as { redirect_to?: string } | null;
      window.location.assign(body?.redirect_to || '/users/sign_in');
    }
    throw new UnauthorizedError(response.error || 'Unauthorized');
  }

  if (!response.ok || response.data == null) {
    const error: ApiError = new Error(response.error || 'Request failed');
    error.status = response.status;
    throw error;
  }

  return response.data;
};
